import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import OrderRowsSkeleton from '../OrderRowsSkeleton';
import { useI18n, useLogger, useSelector } from '~Hooks';
import { formatSearchSum } from '~Scenes/Handy/Scenes/History/utils';
import { Box } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import OrderRow from '../OrderRow';
import { useTableUniverse } from '~Components/TableUniverse';
import { Cheque } from '@baemingo/table-events/dist/types';
import ChequeRow from '../ChequeRow';
import { AdvancedFilterValues } from './FilterSection/AdvancedFilters';
import FilterSection from './FilterSection';
import {
  AdvancedSearchOrders,
  AdvancedSearchOrders_operativeViewer_advancedSearchOrders_orders,
  AdvancedSearchOrdersVariables,
} from '../__queries__';
import { SEARCH_ORDERS_QUERY } from '../queries';

/** Boolean advanced-filter keys are stored as 'true' in the URL (and omitted when false) */
const BOOLEAN_FILTER_KEYS: (keyof AdvancedFilterValues)[] = ['hasDiscount', 'hasTip'];

/** String advanced-filter keys are stored verbatim in the URL (and omitted when empty) */
const STRING_FILTER_KEYS: (keyof AdvancedFilterValues)[] = [
  'amountMin',
  'amountMax',
  'discountMin',
  'discountMax',
  'tipMin',
  'tipMax',
  'staffId',
  'paymentMethod',
  'discountId',
];

/** Read the advanced filters back out of the URL params (falling back to empty/false) */
const parseAdvancedFilters = (params: URLSearchParams): AdvancedFilterValues => {
  const result = {} as AdvancedFilterValues;
  STRING_FILTER_KEYS.forEach((key) => {
    (result[key] as string) = params.get(key) ?? '';
  });
  BOOLEAN_FILTER_KEYS.forEach((key) => {
    (result[key] as boolean) = params.get(key) === 'true';
  });
  return result;
};

const OrdersListContainer = styled(Box)`
  overflow-y: auto;
  height: 100%;
  padding-bottom: 32px;
`;

/** Stays hidden then pops in (no fade) after 100ms, so it never flashes before orders arrive */
const EmptyState = styled(Box)`
  opacity: 0;
  animation: fade-in 100ms steps(1, end) forwards;
`;

export interface ChequeWithTableCode extends Cheque {
  tableCode?: string;
}

/** Locale mapping to match moment's format (same as in Translations/index.ts) */
const LOCALE_MAP: Record<string, string> = {
  se: 'sv-SE',
  en: 'en-GB',
  no: 'nb-NO',
  es: 'es-ES',
  dk: 'da-DK',
  tr: 'tr-TR',
};

interface Props {
  onOrderClick: (orderId: string) => void;
  onChequeClick: (chequeId: string) => void;
}

const HistoryOrdersList: FC<Props> = ({ onOrderClick, onChequeClick }) => {
  const { i18n } = useI18n();
  const logger = useLogger('history-orders-list');

  const location = useLocation();
  const history = useHistory();

  const locale = useSelector((state) => state.locale);

  const isHandy = window.innerWidth <= 600;

  /** Format date as "Today", "Yesterday", "Last Wednesday", or readable date (similar to moment's calendar()) */
  const formatDateLabel = useCallback(
    (dateString: string): string => {
      const luxonLocale = LOCALE_MAP[locale] || 'en-GB';
      const orderDate = DateTime.fromISO(dateString)
        .startOf('day')
        .setLocale(luxonLocale);
      const today = DateTime.now().startOf('day');
      const diffDays = Math.floor(today.diff(orderDate, 'days').days);

      /** Same day */
      if (diffDays === 0) {
        return i18n('General.Today');
      }
      /** Yesterday */
      if (diffDays === 1) {
        return i18n('General.Yesterday');
      }
      /** Last 6 days (2-6 days ago) - show "Last Monday", "Last Tuesday", etc. */
      if (diffDays >= 2 && diffDays <= 6) {
        const dayName = orderDate.toLocaleString({ weekday: 'long' });
        return `${i18n('General.Last')} ${dayName}`;
      }
      /** Everything else - show date in DD/MM/YYYY format */
      return orderDate.toFormat('dd/MM/yyyy');
    },
    [locale, i18n],
  );

  const venueId = useSelector((state) => state.venueId);
  const tables = useSelector((state) => state.tables);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedDate = searchParams.get('date') ?? null;

  /**
   * Selection is read here (once) and passed down as a boolean prop, so the rows
   * don't each subscribe to the router. That way changing the selected id only
   * re-renders the two affected rows instead of the whole list.
   */
  const selectedOrderId = searchParams.get('orderId');
  const selectedChequeId = searchParams.get('chequeId');

  const formattedSearchSum = formatSearchSum(searchParams.get('searchsum') ?? null);
  const formattedSearchTip = formatSearchSum(searchParams.get('searchtip') ?? null);

  // In progress orders
  const universe = useTableUniverse();

  const availableCheques: ChequeWithTableCode[] = useMemo(() => {
    // Search variables derived from URL params
    const searchVariables = {
      easyId: searchParams.get('searchterm') ?? undefined,
      sum: formattedSearchSum,
      startDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .startOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : undefined,
      endDate: selectedDate
        ? DateTime.fromISO(selectedDate)
            .endOf('day')
            .plus({ hours: 5 })
            .toISO() ?? undefined
        : undefined,
    };

    let cheques: ChequeWithTableCode[] = [...universe.cheques]
      .filter((c) => universe.payments?.some((p) => p.chequeId === c.id))
      .sort(
        (a, b) =>
          DateTime.fromISO(b.createdAt!).toMillis() - DateTime.fromISO(a.createdAt!).toMillis(),
      );

    if (tables) {
      cheques = cheques.map((c) => {
        // Make object extensible
        c = { ...c };
        c.tableCode = tables.find((t) => t.id === c.tableId)?.code;
        return c;
      });

      if (searchVariables.easyId) {
        cheques = cheques.filter((c) => c.tableCode === searchVariables.easyId);
      }
    }

    if (searchVariables.sum) {
      cheques = cheques.filter((c) => c.paidAmount === searchVariables.sum);
    }

    return cheques;
  }, [
    searchParams,
    formattedSearchSum,
    selectedDate,
    tables,
    universe.cheques,
    universe.payments,
  ]);

  /**
   * Stable handlers (read the current search from history at call time) so FilterSection
   * stays memoized and doesn't re-render when unrelated state like the selection changes.
   */
  const onSearchParamChange = useCallback(
    (key: 'searchterm' | 'searchsum' | 'searchtip', value: string) => {
      const params = new URLSearchParams(history.location.search);
      params.set(key, value);
      history.push({ search: params.toString() });
    },
    [history],
  );

  const onDateChange = useCallback(
    (date: Date | null) => {
      const params = new URLSearchParams(history.location.search);
      if (date) {
        params.set('date', date.toISOString());
      } else {
        params.delete('date');
      }
      history.push({ search: params.toString() });
    },
    [history],
  );

  /**
   * Advanced filters live in the URL so they survive navigation (important on handheld).
   * The key is a stable string of just the advanced-filter params, so the memoized object
   * reference only changes when a filter actually changes — not on unrelated URL changes
   * like selecting an order (keeps FilterSection memoized).
   */
  const advancedFiltersKey = [...STRING_FILTER_KEYS, ...BOOLEAN_FILTER_KEYS]
    .map((key) => `${key}=${searchParams.get(key) ?? ''}`)
    .join('&');

  const advancedFilters = useMemo(
    () => parseAdvancedFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advancedFiltersKey],
  );

  /** Persist the advanced filters to the URL (omitting empty strings / false booleans) */
  const onAdvancedFiltersChange = useCallback(
    (filters: AdvancedFilterValues) => {
      const params = new URLSearchParams(history.location.search);
      STRING_FILTER_KEYS.forEach((key) => {
        const value = filters[key] as string;
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      BOOLEAN_FILTER_KEYS.forEach((key) => {
        if (filters[key]) {
          params.set(key, 'true');
        } else {
          params.delete(key);
        }
      });
      history.push({ search: params.toString() });
    },
    [history],
  );

  /**
   * Build an IntRangeInput from two raw currency strings.
   * Returns undefined when neither bound is set so the field is left out of the query.
   */
  const buildRange = (min: string, max: string) => {
    const minValue = formatSearchSum(min || null);
    const maxValue = formatSearchSum(max || null);
    return minValue != null || maxValue != null ? { min: minValue, max: maxValue } : undefined;
  };

  /** Amount/tip prefer the advanced range, falling back to the simple search inputs */
  const amountRange =
    buildRange(advancedFilters.amountMin, advancedFilters.amountMax) ??
    (formattedSearchSum ? { min: formattedSearchSum, max: formattedSearchSum } : undefined);
  const discountRange = buildRange(advancedFilters.discountMin, advancedFilters.discountMax);
  const tipRange =
    buildRange(advancedFilters.tipMin, advancedFilters.tipMax) ??
    (formattedSearchTip ? { min: formattedSearchTip, max: formattedSearchTip } : undefined);

  const [orders, setOrders] = useState<
    AdvancedSearchOrders_operativeViewer_advancedSearchOrders_orders[]
  >([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  /** If there is no orders to fetch, don't show the button anymore */
  const [hideLoadMore, setHideLoadMore] = useState<boolean>(false);
  const ORDERS_PER_FETCH = 50; // Fetch first 50 then fetch 50 more each time we press "Show more"

  /** Scroll container, used as the IntersectionObserver root */
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** Sentinel element rendered at the end of the list; when it scrolls into view we fetch more */
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const ordersQuery = useQuery<AdvancedSearchOrders, AdvancedSearchOrdersVariables>(
    SEARCH_ORDERS_QUERY,
    {
      skip: !venueId,
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          venueIds: [venueId!],

          easyIds: searchParams.get('searchterm') ? [searchParams.get('searchterm')!] : undefined,

          amount: amountRange,

          /** Pagination */
          first: ORDERS_PER_FETCH,
          skip: pageIndex * ORDERS_PER_FETCH,
          includeTotalCount: true,

          /** Date range filter (createdAt_gt = from, createdAt_lt = to) */
          createdAt_gt: selectedDate
            ? DateTime.fromISO(selectedDate)
                .startOf('day')
                .plus({ hours: 5 })
                .toISO() ?? undefined
            : undefined,
          createdAt_lt: selectedDate
            ? DateTime.fromISO(selectedDate)
                .endOf('day')
                .plus({ hours: 5 })
                .toISO() ?? undefined
            : undefined,

          /** Advanced filters */
          discount: discountRange,
          tip: tipRange,
          hasDiscount: advancedFilters.hasDiscount || undefined,
          hasTip: advancedFilters.hasTip || undefined,
          staffIds: advancedFilters.staffId ? [advancedFilters.staffId] : undefined,
          paymentMethods: advancedFilters.paymentMethod
            ? [advancedFilters.paymentMethod]
            : undefined,
          discountIds: advancedFilters.discountId ? [advancedFilters.discountId] : undefined,

          /** Filters not yet wired up in the UI, hardcoded for now so all fields are visible */
          bookingIds: undefined,
          discountSources: undefined,
          invoiceTargetIds: undefined,
          origins: undefined,
          serviceTypes: [],
          tableIds: undefined,
          userIds: undefined,
          zoneIds: undefined,
        },
      },
    },
  );

  useEffect(() => {
    // Only update orders when the query is not loading (i.e., data is fresh)
    if (ordersQuery.loading) return;

    const newlyFetchedOrders = ordersQuery.data?.operativeViewer.advancedSearchOrders.orders ?? [];

    // If pageIndex is 0, we're starting fresh (either first load or filter changed)
    if (pageIndex === 0) {
      setOrders(newlyFetchedOrders);
    } else {
      // Append to existing orders for pagination
      setOrders((prev) => [...prev, ...newlyFetchedOrders]);
    }

    /** If there is less than 50 we know that we have reached the end */
    if (newlyFetchedOrders.length < 50) {
      setHideLoadMore(true);
    }
  }, [pageIndex, ordersQuery.data, ordersQuery.loading]);

  /** Reset pagination and orders when filter changes */
  useEffect(() => {
    setOrders([]);
    setPageIndex(0);
    setHideLoadMore(false);
  }, [
    searchParams.get('searchterm'),
    searchParams.get('searchsum'),
    searchParams.get('searchtip'),
    selectedDate,
    advancedFiltersKey,
  ]);

  const groupedOrdersByDate = useMemo(() => {
    return (orders || []).reduce<{
      [date: string]: AdvancedSearchOrders_operativeViewer_advancedSearchOrders_orders[];
    }>((acc, order) => {
      const date = order.createdAt?.slice(0, 10); // group by date (not time)

      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(order);

      return acc;
    }, {});
  }, [orders]);

  const filteredAvailableCheques = useMemo(() => {
    return availableCheques.filter((cheque) => {
      // Only show for the selected date if any
      if (selectedDate) {
        const chequeDate = DateTime.fromISO(cheque.createdAt!);
        const selected = DateTime.fromISO(selectedDate);
        return (
          chequeDate.hasSame(selected, 'day') &&
          chequeDate.hasSame(selected, 'month') &&
          chequeDate.hasSame(selected, 'year')
        );
      }
      return true;
    });
  }, [availableCheques, selectedDate]);

  // If no order is selected auto select the first one
  useEffect(() => {
    // Includes history is for tablet, in handheld we don't want to auto select and the url in handly does not include "history" in the path
    if (
      orders.length > 0 &&
      !searchParams.get('orderId') &&
      !searchParams.get('chequeId') &&
      location.pathname.includes('/history')
    ) {
      onOrderClick(orders[0].id);
    }
  }, [orders, searchParams, onOrderClick, onChequeClick, location]);

  /** Memoized list of open cheque rows, only recomputed when the cheques change */
  const chequeRows = useMemo(() => {
    if (filteredAvailableCheques.length === 0) {
      return null;
    }

    return (
      <Box marginBottom="medium">
        <Paragraph level={4} color="var(--colorWhite)" bottom={8}>
          {i18n('History.OpenCheques')}
        </Paragraph>
        {filteredAvailableCheques.map((cheque) => (
          <ChequeRow
            key={cheque.id}
            cheque={cheque}
            isSelected={cheque.id === selectedChequeId}
            onChequeClick={onChequeClick}
          />
        ))}
      </Box>
    );
  }, [filteredAvailableCheques, onChequeClick, i18n, selectedChequeId]);

  /** Memoized list of order rows grouped by date, only recomputed when the orders change */
  const orderRows = useMemo(() => {
    return Object.entries(groupedOrdersByDate).map(([date, groupedOrders]) => (
      <Box marginBottom="medium" key={date}>
        <Paragraph level={4} color="var(--colorWhite)" bottom={8}>
          {formatDateLabel(date)}
        </Paragraph>
        {groupedOrders.map((order) => (
          <OrderRow
            key={order.id}
            order={order}
            isSelected={order.id === selectedOrderId}
            onOrderClick={onOrderClick}
          />
        ))}
      </Box>
    ));
  }, [groupedOrdersByDate, onOrderClick, formatDateLabel, selectedOrderId]);

  /**
   * Infinite scroll: observe a sentinel at the bottom of the list and fetch the
   * next page once it scrolls into view (replaces the old "Show more" button).
   */
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;

    /** Nothing to observe yet, or there's nothing more to fetch */
    if (!sentinel || hideLoadMore || orders.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        /** Only fetch when the sentinel is visible and we're not already loading */
        if (entry.isIntersecting && !ordersQuery.loading) {
          logger.info('Sentinel reached, fetching next page', {
            nextPageIndex: pageIndex + 1,
            loadedOrders: orders.length,
          });
          setPageIndex((prev) => prev + 1);
        }
      },
      {
        /** Scroll within the list container, and start fetching slightly before the very end */
        root: containerRef.current,
        rootMargin: '200px',
      },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hideLoadMore, ordersQuery.loading, orders.length, pageIndex, logger]);

  const showEmpty =
    !ordersQuery.loading && orders.length === 0 && filteredAvailableCheques.length === 0;

  return (
    <OrdersListContainer ref={containerRef} marginTop={isHandy ? 'medium' : undefined}>
      <FilterSection
        searchTerm={searchParams.get('searchterm') ?? ''}
        searchSum={searchParams.get('searchsum') ?? ''}
        searchTip={searchParams.get('searchtip') ?? ''}
        selectedDate={selectedDate}
        advancedFilters={advancedFilters}
        onAdvancedFiltersChange={onAdvancedFiltersChange}
        onSearchParamChange={onSearchParamChange}
        onDateChange={onDateChange}
      />

      {chequeRows}

      {ordersQuery.loading && pageIndex === 0 && <OrderRowsSkeleton />}

      {showEmpty && (
        <EmptyState
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding="xl"
          textAlign="center">
          <Heading level={3} color="var(--colorWhite)" bottom={8}>
            {i18n('History.NoOrdersFound')}
          </Heading>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('History.NoOrdersFoundSubtitle')}
          </Paragraph>
        </EmptyState>
      )}

      {orderRows}

      {orders.length > 0 && !hideLoadMore && (
        <div ref={loadMoreSentinelRef}>{ordersQuery.loading && <OrderRowsSkeleton small />}</div>
      )}
    </OrdersListContainer>
  );
};

export default HistoryOrdersList;
